import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import ErrorMessage from '../components/ErrorMessage'
import LoadingProgress from '../components/LoadingProgress'
import { history } from '../store'

import { 
  fetchIncidence, 
  updateIncidence,
  reviewIncidence,
  repairIncidence,
  resolveIncidence
} from '../actions/incidences'

import '../styles/SupportTicketAdminView.css'

const STATUS_LABELS = {
  OPEN: 'Abierta',
  IN_REVIEW: 'En Revisión',
  IN_REPAIR: 'En Reparación',
  RESOLVED: 'Resuelta',
  CLOSED: 'Cerrada',
  REPAIR: 'En Reparación'
}

const PRIORITY_LABELS = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
}

const CATEGORY_LABELS = {
  Minor: 'Menor',
  Breakdown: 'Avería',
  UrgentBreakdown: 'Avería Urgente',
  COINS: 'Pago Monedas',
  BILLS: 'Pago Billetes',
  TPV: 'Pago Tarjeta',
  SCREEN_OFF: 'Pantalla Apagada',
  SCREEN_BREAK: 'Pantalla Rota',
  SCREEN_TOUCH: 'Táctil No Responde',
  PRINTER_TICKET: 'Impresora Ticket',
  KIOSC_DOOR: 'Kiosco Puerta',
  KIOSC_OFF: 'Kiosco No Enciende',
  OTHER: 'Otro',
}

class IncidenceDetailsView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      showActuationModal: false,
      actuationAction: null, // 'repair' or 'resolve'
      actuationText: '',
    }

    this.handleActuationSubmit = this.handleActuationSubmit.bind(this)
  }

  componentDidMount() {
    this.props.fetchIncidence(this.props.match.params.number)
  }

  componentDidUpdate(prevProps) {
    if (this.props.incidenceNeedRefresh && !prevProps.incidenceNeedRefresh) {
      this.props.fetchIncidence(this.props.match.params.number)
    }
  }

  handleReview() {
    this.props.reviewIncidence(this.props.match.params.number)
  }

  handleOpenRepairModal() {
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'repair',
      actuationText: ''
    })
  }

  handleOpenResolveModal() {
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'resolve',
      actuationText: ''
    })
  }

  handleActuationSubmit() {
    const { actuationAction, actuationText } = this.state
    const number = this.props.match.params.number
    
    if (!actuationText.trim()) {
      alert('Por favor, introduce el texto de actuación')
      return
    }

    if (actuationAction === 'repair') {
      this.props.repairIncidence(number, actuationText)
    } else if (actuationAction === 'resolve') {
      this.props.resolveIncidence(number, actuationText)
    }

    this.setState({ 
      showActuationModal: false, 
      actuationAction: null, 
      actuationText: '' 
    })
  }

  render() {
    const { role, incidence, error, loading, incidenceNeedRefresh } = this.props

    if (error) {
      return <ErrorMessage message={error.message} />
    }

    if (!incidence || loading || incidenceNeedRefresh) {
      return <LoadingProgress />
    }

    const isAdmin = role === 'ADMIN'
    const isResolved = incidence.Status === 'RESOLVED' || incidence.Status === 'CLOSED'

    return (
      <div className="st-detail-view">
        {/* Header */}
        <div className="st-detail-header">
          <div className="st-detail-header-top">
            <h1>
              <i className="fas fa-exclamation-triangle"></i>
              Incidencia #{incidence.number}
            </h1>
            <Link to="/incidences" className="st-detail-back-btn">
              <i className="fas fa-arrow-left"></i>Volver
            </Link>
          </div>
          <div className="st-detail-header-badges">
            <span className={`ticket-status-badge ${incidence.Status}`}>
              {STATUS_LABELS[incidence.Status] || incidence.Status}
            </span>
            {incidence.Priority && 
              <span className={`ticket-priority-badge ${incidence.Priority}`}>
                {PRIORITY_LABELS[incidence.Priority] || incidence.Priority}
              </span>
            }
          </div>
        </div>

        {/* Info grid */}
        <div className="st-detail-grid">
          <div className="st-detail-card">
            <div className="st-detail-card-title">
              <i className="fas fa-info-circle"></i> Información General
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-field">
                <span className="st-detail-field-label">Número</span>
                <span className="st-detail-field-value">#{incidence.number}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Código</span>
                <span className="st-detail-field-value">{incidence.Code || 'N/A'}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Categoría</span>
                <span className="st-detail-field-value">{CATEGORY_LABELS[incidence.Category] || incidence.Category}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Cliente</span>
                <span className="st-detail-field-value">{incidence.Customer && incidence.Customer.Fullname ? incidence.Customer.Fullname : 'N/A'}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Máquina</span>
                <span className="st-detail-field-value">{incidence.Machine ? incidence.Machine.Serial : 'N/A'}</span>
              </div>
              {incidence.SupportTicket &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">Ticket Origen</span>
                  <span className="st-detail-field-value">
                    <Link to={`/supporttickets/${incidence.SupportTicket._id || incidence.SupportTicket}`}>
                      #{incidence.SupportTicket.number || 'Ver'}
                    </Link>
                  </span>
                </div>
              }
            </div>
          </div>

          <div className="st-detail-card">
            <div className="st-detail-card-title">
              <i className="fas fa-calendar"></i> Fechas y Asignaciones
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-field">
                <span className="st-detail-field-label">Fecha Apertura</span>
                <span className="st-detail-field-value">
                  {new Date(incidence.OpenAt || incidence.createdAt).toLocaleString()}
                  {incidence.OpenBy ? ` — ${incidence.OpenBy.fullname}` : ''}
                </span>
              </div>
              {incidence.InReviewAt &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">En Revisión</span>
                  <span className="st-detail-field-value">
                    {new Date(incidence.InReviewAt).toLocaleString()}
                    {incidence.InReviewBy ? ` — ${incidence.InReviewBy.fullname}` : ''}
                  </span>
                </div>
              }
              {incidence.InRepairAt &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">En Reparación</span>
                  <span className="st-detail-field-value">
                    {new Date(incidence.InRepairAt).toLocaleString()}
                    {incidence.InRepairBy ? ` — ${incidence.InRepairBy.fullname}` : ''}
                  </span>
                </div>
              }
              {incidence.ResolvedAt &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">Resuelta</span>
                  <span className="st-detail-field-value">
                    {new Date(incidence.ResolvedAt).toLocaleString()}
                    {incidence.ResolvedBy ? ` — ${incidence.ResolvedBy.fullname}` : ''}
                  </span>
                </div>
              }
              {incidence.ResponseForecastTime &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">Tiempo Respuesta Previsto</span>
                  <span className="st-detail-field-value">{incidence.ResponseForecastTime}h</span>
                </div>
              }
              {incidence.RepairingForecastTime &&
                <div className="st-detail-field">
                  <span className="st-detail-field-label">Tiempo Reparación Previsto</span>
                  <span className="st-detail-field-value">{incidence.RepairingForecastTime}h</span>
                </div>
              }
            </div>
          </div>

          <div className="st-detail-card">
            <div className="st-detail-card-title">
              <i className="fas fa-user"></i> Contacto Cliente
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-field">
                <span className="st-detail-field-label">Nombre</span>
                <span className="st-detail-field-value">{incidence.Customer && incidence.Customer.Fullname ? incidence.Customer.Fullname : 'N/A'}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Email</span>
                <span className="st-detail-field-value">{incidence.Customer && incidence.Customer.Email ? incidence.Customer.Email : 'N/A'}</span>
              </div>
              <div className="st-detail-field">
                <span className="st-detail-field-label">Teléfono</span>
                <span className="st-detail-field-value">{incidence.Customer && incidence.Customer.Phone ? incidence.Customer.Phone : 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="st-detail-card" style={{ marginBottom: 24 }}>
          <div className="st-detail-card-title">
            <i className="fas fa-file-alt"></i> Descripción
          </div>
          <div className="st-detail-card-body">
            <div className="st-detail-description">
              {incidence.Description || 'Sin descripción'}
            </div>
          </div>
        </div>

        {/* Repair Actuation */}
        {incidence.RepairActuation &&
          <div className="st-detail-card" style={{ marginBottom: 24 }}>
            <div className="st-detail-card-title">
              <i className="fas fa-wrench"></i> Actuación de Reparación
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-description">
                {incidence.RepairActuation}
              </div>
            </div>
          </div>
        }

        {/* Resolution Actuation */}
        {incidence.ResolvedActuation &&
          <div className="st-detail-card" style={{ marginBottom: 24 }}>
            <div className="st-detail-card-title">
              <i className="fas fa-check-circle"></i> Resolución
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-description">
                {incidence.ResolvedActuation}
              </div>
            </div>
          </div>
        }

        {/* Old fields for backwards compatibility */}
        {incidence.Actuation && !incidence.RepairActuation &&
          <div className="st-detail-card" style={{ marginBottom: 24 }}>
            <div className="st-detail-card-title">
              <i className="fas fa-tools"></i> Actuación
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-description">
                {incidence.Actuation}
              </div>
            </div>
          </div>
        }

        {incidence.Resolution && !incidence.ResolvedActuation &&
          <div className="st-detail-card" style={{ marginBottom: 24 }}>
            <div className="st-detail-card-title">
              <i className="fas fa-check"></i> Resolución (Legacy)
            </div>
            <div className="st-detail-card-body">
              <div className="st-detail-description">
                {incidence.Resolution}
              </div>
            </div>
          </div>
        }

        {/* Actions */}
        {isAdmin && !isResolved &&
          <div className="st-detail-actions-bar">
            <h3><i className="fas fa-cogs"></i> Acciones</h3>
            <div className="st-detail-actions-row">
              {/* OPEN -> only Review button */}
              {incidence.Status === 'OPEN' &&
                <button className="st-detail-action-btn review"
                  onClick={() => this.handleReview()}>
                  <i className="fas fa-eye"></i>Revisar
                </button>
              }
              {/* IN_REVIEW -> Repair or Resolve */}
              {incidence.Status === 'IN_REVIEW' &&
                <>
                  <button className="st-detail-action-btn repair"
                    onClick={() => this.handleOpenRepairModal()}>
                    <i className="fas fa-wrench"></i>Pasar a Reparación
                  </button>
                  <button className="st-detail-action-btn resolve"
                    onClick={() => this.handleOpenResolveModal()}>
                    <i className="fas fa-check"></i>Resolver
                  </button>
                </>
              }
              {/* IN_REPAIR -> Resolve */}
              {(incidence.Status === 'IN_REPAIR' || incidence.Status === 'REPAIR') &&
                <button className="st-detail-action-btn resolve"
                  onClick={() => this.handleOpenResolveModal()}>
                  <i className="fas fa-check"></i>Resolver
                </button>
              }
            </div>
          </div>
        }

        {isAdmin &&
          <div className="st-detail-delete-row">
            <Link to={`/incidences/${incidence.number}/edit`}>
              <button className="btn btn-warning mr-2">
                <i className="fas fa-edit mr-1"></i>Editar
              </button>
            </Link>
          </div>
        }

        {/* Actuation Modal for Repair/Resolve */}
        <Modal isOpen={this.state.showActuationModal} toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
          <ModalHeader toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
            {this.state.actuationAction === 'repair' ? 'Pasar a Reparación' : 'Resolver Incidencia'}
          </ModalHeader>
          <ModalBody>
            <p>
              {this.state.actuationAction === 'repair' 
                ? 'Describe la actuación de reparación a realizar:' 
                : 'Describe cómo se ha resuelto la incidencia:'}
            </p>
            <textarea
              className="form-control"
              rows={4}
              value={this.state.actuationText}
              onChange={(e) => this.setState({ actuationText: e.target.value })}
              placeholder={this.state.actuationAction === 'repair' 
                ? 'Escribe la actuación de reparación...' 
                : 'Escribe la resolución...'}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleActuationSubmit}>
              {this.state.actuationAction === 'repair' ? 'Pasar a Reparación' : 'Resolver'}
            </Button>
            <Button color="secondary" onClick={() => this.setState({ showActuationModal: false, actuationText: '' })}>
              Cancelar
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = state => ({
  incidence: state.incidences.incidence,
  loading: state.incidences.loading,
  error: state.incidences.error,
  role: state.auth.role,
  incidenceNeedRefresh: state.incidences.incidenceNeedRefresh
})

const mapDispatchToProps = dispatch => ({
  fetchIncidence: bindActionCreators(fetchIncidence, dispatch),
  updateIncidence: bindActionCreators(updateIncidence, dispatch),
  reviewIncidence: bindActionCreators(reviewIncidence, dispatch),
  repairIncidence: bindActionCreators(repairIncidence, dispatch),
  resolveIncidence: bindActionCreators(resolveIncidence, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(IncidenceDetailsView)

